<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Grantors (also known as settlors or trustors) are the individuals who
          created the trust and transferred assets into it. They may be alive or
          deceased.
        </p>
      </div>
    </help-drawer>
    <v-form name="Grantors" class="form" data-test-id="grantors-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.GRANTORS.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="grantors-header">
              <span class="headerlight">
                <strong>Grantor information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving grantor information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isGrantorsComplete && formHidden && !isSyncing"
          :submitted="isGrantorsComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            
            <!-- Grantors List -->
            <div v-for="(grantor, index) in grantors" :key="index" class="mb-4">
              <v-card class="pa-4" elevation="0" outlined>
                <v-card-title class="pa-0 mb-4">
                  <span class="text-h6">Grantor {{ index + 1 }}</span>
                  <v-spacer></v-spacer>
                  <v-chip
                    v-if="grantor.status"
                    small
                    :color="grantor.status === 'Alive' ? 'success' : 'grey'"
                    text-color="white"
                  >
                    {{ grantor.status }}
                  </v-chip>
                </v-card-title>
              
                <!-- Status Selection (Always visible) -->
                <v-row class="mb-4">
                  <v-col cols="12">
                    <label class="required mb-2" data-test-id="grantor-status-label">
                      Is this grantor alive or deceased?
                    </label>
                    <v-radio-group
                      v-model="grantor.status"
                      :error-messages="getStatusErrors(index)"
                      hide-details="auto"
                      row
                      @change="handleStatusChange(index)"
                      :data-test-id="`grantor-status-radio-group-${index}`"
                    >
                      <v-radio
                        :ripple="false"
                        class="v-faux-radio half-width first"
                        label="Alive"
                        value="Alive"
                        :data-test-id="`grantor-alive-${index}`"
                      ></v-radio>
                      <v-radio
                        :ripple="false"
                        class="v-faux-radio half-width"
                        label="Deceased"
                        value="Deceased"
                        :data-test-id="`grantor-deceased-${index}`"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
              
                <!-- Basic Information (show only after status is selected) -->
                <v-expand-transition>
                  <div v-if="grantor.status">
                    <v-row>
                  <v-col cols="12" sm="6">
                    <label :for="`firstName-${index}`" class="required" data-test-id="first-name-label">
                      First Name
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="256"
                      :name="`firstName-${index}`"
                      aria-required="true"
                      v-model.trim="grantor.firstName"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.grantors.$each[index].firstName.$touch()"
                      @blur="$v.grantors.$each[index].firstName.$touch()"
                      :error-messages="getFirstNameErrors(index)"
                      hide-details="auto"
                      :data-test-id="`first-name-input-${index}`"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <label :for="`lastName-${index}`" class="required" data-test-id="last-name-label">
                      Last Name
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="256"
                      :name="`lastName-${index}`"
                      aria-required="true"
                      v-model.trim="grantor.lastName"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.grantors.$each[index].lastName.$touch()"
                      @blur="$v.grantors.$each[index].lastName.$touch()"
                      :error-messages="getLastNameErrors(index)"
                      hide-details="auto"
                      :data-test-id="`last-name-input-${index}`"
                    />
                  </v-col>
                </v-row>
              
                <!-- Citizenship and Source of Wealth -->
                <v-row class="mt-2">
                  <v-col cols="12" sm="6">
                    <label :for="`citizenship-${index}`" class="required" data-test-id="citizenship-label">
                      Citizenship
                    </label>
                    <v-select
                      v-model="grantor.citizenship"
                      :items="countries"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      dense
                      hide-details="auto"
                      @change="$v.grantors.$each[index].citizenship.$touch()"
                      :error-messages="getCitizenshipErrors(index)"
                      :data-test-id="`citizenship-select-${index}`"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <label :for="`sourceOfWealth-${index}`" class="required" data-test-id="source-wealth-label">
                      Source of Wealth
                    </label>
                    <v-select
                      v-model="grantor.sourceOfWealth"
                      :items="wealthSources"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      dense
                      hide-details="auto"
                      @change="$v.grantors.$each[index].sourceOfWealth.$touch()"
                      :error-messages="getSourceOfWealthErrors(index)"
                      :data-test-id="`source-wealth-select-${index}`"
                    />
                  </v-col>
                </v-row>
              
                <!-- Alive Grantor Fields -->
                <template v-if="grantor.status === 'Alive'">
                  <!-- Address -->
                  <v-row class="mt-6">
                    <v-col cols="12">
                      <h4 class="mb-3">Address</h4>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <label :for="`street-${index}`" class="required" data-test-id="street-label">
                        Street Address
                      </label>
                      <v-text-field
                        outlined
                        type="text"
                        maxlength="256"
                        :name="`street-${index}`"
                        aria-required="true"
                        v-model.trim="grantor.address.street"
                        :backgroundColor="inputBackgroundColor"
                        @input="$v.grantors.$each[index].address.street.$touch()"
                        @blur="$v.grantors.$each[index].address.street.$touch()"
                        :error-messages="getAddressStreetErrors(index)"
                        hide-details="auto"
                        :data-test-id="`street-input-${index}`"
                      />
                    </v-col>
                  </v-row>
                  
                  <v-row class="mt-2">
                    <v-col cols="12" sm="6">
                      <label :for="`city-${index}`" class="required" data-test-id="city-label">
                        City
                      </label>
                      <v-text-field
                        outlined
                        type="text"
                        maxlength="256"
                        :name="`city-${index}`"
                        aria-required="true"
                        v-model.trim="grantor.address.city"
                        :backgroundColor="inputBackgroundColor"
                        @input="$v.grantors.$each[index].address.city.$touch()"
                        @blur="$v.grantors.$each[index].address.city.$touch()"
                        :error-messages="getAddressCityErrors(index)"
                        hide-details="auto"
                        :data-test-id="`city-input-${index}`"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <label :for="`state-${index}`" class="required" data-test-id="state-label">
                        State
                      </label>
                      <v-select
                        v-model="grantor.address.state"
                        :items="states"
                        :backgroundColor="inputBackgroundColor"
                        outlined
                        dense
                        hide-details="auto"
                        @change="$v.grantors.$each[index].address.state.$touch()"
                        :error-messages="getAddressStateErrors(index)"
                        :data-test-id="`state-select-${index}`"
                      />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <label :for="`zipCode-${index}`" class="required" data-test-id="zip-label">
                        ZIP Code
                      </label>
                      <v-text-field
                        outlined
                        type="text"
                        maxlength="10"
                        :name="`zipCode-${index}`"
                        aria-required="true"
                        v-model="grantor.address.zipCode"
                        :backgroundColor="inputBackgroundColor"
                        @input="formatZipCode(index)"
                        @blur="$v.grantors.$each[index].address.zipCode.$touch()"
                        :error-messages="getAddressZipErrors(index)"
                        hide-details="auto"
                        :data-test-id="`zip-input-${index}`"
                      />
                    </v-col>
                  </v-row>
                  
                  <v-row class="mt-2">
                    <v-col cols="12" sm="6">
                      <label :for="`country-${index}`" class="required" data-test-id="country-label">
                        Country
                      </label>
                      <v-select
                        v-model="grantor.address.country"
                        :items="countries"
                        :backgroundColor="inputBackgroundColor"
                        outlined
                        dense
                        hide-details="auto"
                        @change="$v.grantors.$each[index].address.country.$touch()"
                        :error-messages="getAddressCountryErrors(index)"
                        :data-test-id="`country-select-${index}`"
                      />
                    </v-col>
                  </v-row>
                
                  <!-- Employment Information -->
                  <v-row class="mt-6">
                    <v-col cols="12">
                      <h4 class="mb-3">Employment Information</h4>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <label :for="`employmentStatus-${index}`" class="required" data-test-id="employment-status-label">
                        Employment Status
                      </label>
                      <v-select
                        v-model="grantor.employmentStatus"
                        :items="employmentStatuses"
                        :backgroundColor="inputBackgroundColor"
                        outlined
                        dense
                        hide-details="auto"
                        @change="$v.grantors.$each[index].employmentStatus.$touch()"
                        :error-messages="getEmploymentStatusErrors(index)"
                        :data-test-id="`employment-status-select-${index}`"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <label :for="`employerIndustry-${index}`" class="required" data-test-id="employer-industry-label">
                        Line of Business
                      </label>
                      <v-select
                        v-model="grantor.employerIndustry"
                        :items="businessLines"
                        :backgroundColor="inputBackgroundColor"
                        outlined
                        dense
                        hide-details="auto"
                        @change="$v.grantors.$each[index].employerIndustry.$touch()"
                        :error-messages="getEmployerIndustryErrors(index)"
                        :data-test-id="`employer-industry-select-${index}`"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <label :for="`annualIncome-${index}`" class="required" data-test-id="annual-income-label">
                        Annual Income
                      </label>
                      <v-text-field
                        outlined
                        type="number"
                        :name="`annualIncome-${index}`"
                        aria-required="true"
                        v-model.number="grantor.annualIncome"
                        prefix="$"
                        :backgroundColor="inputBackgroundColor"
                        @input="$v.grantors.$each[index].annualIncome.$touch()"
                        @blur="$v.grantors.$each[index].annualIncome.$touch()"
                        :error-messages="getAnnualIncomeErrors(index)"
                        hide-details="auto"
                        :data-test-id="`annual-income-input-${index}`"
                      />
                    </v-col>
                  </v-row>
                </template>
              
                <!-- Deceased Grantor Fields -->
                <template v-else-if="grantor.status === 'Deceased'">
                  <v-row class="mt-4">
                    <v-col cols="12" sm="6">
                      <label :for="`dateOfDeath-${index}`" class="required" data-test-id="date-death-label">
                        Date of Death
                      </label>
                      <v-text-field
                        v-model="grantor.dateOfDeath"
                        :placeholder="INPUT_DATE_FMT"
                        outlined
                        :name="`dateOfDeath-${index}`"
                        maxlength="8"
                        :backgroundColor="inputBackgroundColor"
                        @input="$v.grantors.$each[index].dateOfDeath.$touch()"
                        @blur="standardizeDateOfDeath(index)"
                        :error-messages="getDateOfDeathErrors(index)"
                        hide-details="auto"
                        inputmode="numeric"
                        :data-test-id="`date-death-input-${index}`"
                      >
                        <template v-slot:append>
                          <span class="cal-icon" data-test-id="calendar-icon">
                            <v-icon small color="#9b9b9b">
                              fas fa-calendar-alt
                            </v-icon>
                          </span>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </template>
                  </div>
                </v-expand-transition>
              
                <!-- Remove Grantor Button -->
                <v-row v-if="grantors.length > 1" class="mt-4">
                  <v-col cols="12" class="text-right">
                    <v-btn
                      outlined
                      color="error"
                      @click="removeGrantor(index)"
                      :data-test-id="`remove-grantor-button-${index}`"
                    >
                      <v-icon left small>mdi-delete</v-icon>
                      Remove Grantor
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </div>
            
            <!-- Add Grantor Button -->
            <v-row class="mt-2 mb-4">
              <v-col cols="12">
                <v-btn
                  outlined
                  color="primary"
                  @click="addGrantor"
                  data-test-id="add-grantor-button"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Grantor
                </v-btn>
              </v-col>
            </v-row>
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="grantors-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, STATES, COUNTRIES, INPUT_DATE_FMT, validInputDate, WEALTH_SOURCE_LIST, validateZip } from '@/common/constants';
import { required, requiredIf, numeric } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

const createEmptyGrantor = () => ({
  status: null,
  firstName: null,
  lastName: null,
  address: {
    street: null,
    city: null,
    state: null,
    zipCode: null,
    country: 'US',
  },
  citizenship: null,
  sourceOfWealth: null,
  employmentStatus: null,
  employerIndustry: null,
  annualIncome: null,
  dateOfDeath: null,
});

export default {
  name: 'Grantors',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    grantors: [createEmptyGrantor()],
    employmentStatuses: [
      'Employed',
      'Self-Employed',
      'Retired',
      'Unemployed',
      'Student',
      'Other',
    ],
    states: STATES,
    countries: COUNTRIES,
    wealthSources: Object.values(WEALTH_SOURCE_LIST),
    businessLines: [
      'Business Owner',
      'Executive',
      'Medical Professional',
      'Legal Professional',
      'Accounting Professional',
      'Financial Services',
      'It Professional',
      'Educator',
      'Administrative services',
      'Sales Marketing',
      'Consultant',
      'Other Professional',
    ],
    MENUS: MENUS,
    INPUT_DATE_FMT: INPUT_DATE_FMT,
  }),
  
  validations() {
    const validations = {
      grantors: {
        $each: {
          status: { required },
          firstName: { required },
          lastName: { required },
          citizenship: { required },
          sourceOfWealth: { required },
          // Alive grantor validations
          address: {
            street: { 
              required: requiredIf(function() {
                const grantor = this.grantors[this.$v.grantors.$each.$iter];
                return grantor && grantor.status === 'Alive';
              })
            },
            city: { 
              required: requiredIf(function() {
                const grantor = this.grantors[this.$v.grantors.$each.$iter];
                return grantor && grantor.status === 'Alive';
              })
            },
            state: { 
              required: requiredIf(function() {
                const grantor = this.grantors[this.$v.grantors.$each.$iter];
                return grantor && grantor.status === 'Alive';
              })
            },
            zipCode: { 
              required: requiredIf(function() {
                const grantor = this.grantors[this.$v.grantors.$each.$iter];
                return grantor && grantor.status === 'Alive';
              }),
              validateZip
            },
            country: { 
              required: requiredIf(function() {
                const grantor = this.grantors[this.$v.grantors.$each.$iter];
                return grantor && grantor.status === 'Alive';
              })
            },
          },
          employmentStatus: { 
            required: requiredIf(function() {
              const grantor = this.grantors[this.$v.grantors.$each.$iter];
              return grantor && grantor.status === 'Alive';
            })
          },
          employerIndustry: { 
            required: requiredIf(function() {
              const grantor = this.grantors[this.$v.grantors.$each.$iter];
              return grantor && grantor.status === 'Alive';
            })
          },
          annualIncome: { 
            required: requiredIf(function() {
              const grantor = this.grantors[this.$v.grantors.$each.$iter];
              return grantor && grantor.status === 'Alive';
            }),
            numeric,
          },
          // Deceased grantor validations
          dateOfDeath: { 
            required: requiredIf(function() {
              const grantor = this.grantors[this.$v.grantors.$each.$iter];
              return grantor && grantor.status === 'Deceased';
            }),
            validInputDate,
          },
        }
      },
      validationGroup: ['grantors'],
    };
    
    return validations;
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isGrantorsComplete() {
      const page = this.getPage(MENUS.CLIENT.GRANTORS.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.GRANTORS.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'What does this mean?';
    },
  },
  
  methods: {
    // Error getters for dynamic validation
    getStatusErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].status.$dirty) return errors;
      !this.$v.grantors.$each[index].status.required && errors.push('Please select whether the grantor is alive or deceased');
      return errors;
    },
    
    getFirstNameErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].firstName.$dirty) return errors;
      !this.$v.grantors.$each[index].firstName.required && errors.push('First name is required');
      return errors;
    },
    
    getLastNameErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].lastName.$dirty) return errors;
      !this.$v.grantors.$each[index].lastName.required && errors.push('Last name is required');
      return errors;
    },
    
    getCitizenshipErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].citizenship.$dirty) return errors;
      !this.$v.grantors.$each[index].citizenship.required && errors.push('Citizenship is required');
      return errors;
    },
    
    getSourceOfWealthErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].sourceOfWealth.$dirty) return errors;
      !this.$v.grantors.$each[index].sourceOfWealth.required && errors.push('Source of wealth is required');
      return errors;
    },
    
    getAddressStreetErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].address.street.$dirty) return errors;
      !this.$v.grantors.$each[index].address.street.required && errors.push('Street address is required');
      return errors;
    },
    
    getAddressCityErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].address.city.$dirty) return errors;
      !this.$v.grantors.$each[index].address.city.required && errors.push('City is required');
      return errors;
    },
    
    getAddressStateErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].address.state.$dirty) return errors;
      !this.$v.grantors.$each[index].address.state.required && errors.push('State is required');
      return errors;
    },
    
    getAddressZipErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].address.zipCode.$dirty) return errors;
      !this.$v.grantors.$each[index].address.zipCode.required && errors.push('ZIP code is required');
      !this.$v.grantors.$each[index].address.zipCode.validateZip && errors.push('ZIP code must be NNNNN or NNNNN-NNNN');
      return errors;
    },
    
    getAddressCountryErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].address.country.$dirty) return errors;
      !this.$v.grantors.$each[index].address.country.required && errors.push('Country is required');
      return errors;
    },
    
    getEmploymentStatusErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].employmentStatus.$dirty) return errors;
      !this.$v.grantors.$each[index].employmentStatus.required && errors.push('Employment status is required');
      return errors;
    },
    
    getEmployerIndustryErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].employerIndustry.$dirty) return errors;
      !this.$v.grantors.$each[index].employerIndustry.required && errors.push('Line of business is required');
      return errors;
    },
    
    getAnnualIncomeErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].annualIncome.$dirty) return errors;
      !this.$v.grantors.$each[index].annualIncome.required && errors.push('Annual income is required');
      !this.$v.grantors.$each[index].annualIncome.numeric && errors.push('Please enter a valid number');
      return errors;
    },
    
    getDateOfDeathErrors(index) {
      const errors = [];
      if (!this.$v.grantors.$each[index]) return errors;
      if (!this.$v.grantors.$each[index].dateOfDeath.$dirty) return errors;
      !this.$v.grantors.$each[index].dateOfDeath.required && errors.push('Date of death is required');
      !this.$v.grantors.$each[index].dateOfDeath.validInputDate && errors.push('Please enter a valid date');
      return errors;
    },
    
    handleStatusChange(index) {
      this.$v.grantors.$each[index].status.$touch();
      
      // Reset status-specific fields when switching
      const grantor = this.grantors[index];
      if (grantor.status === 'Alive') {
        // Clear deceased-specific fields
        grantor.dateOfDeath = null;
      } else if (grantor.status === 'Deceased') {
        // Clear alive-specific fields
        grantor.address = {
          street: null,
          city: null,
          state: null,
          zipCode: null,
          country: 'US',
        };
        grantor.employmentStatus = null;
        grantor.employerIndustry = null;
        grantor.annualIncome = null;
      }
      
      // Reset validation for the grantor
      if (this.$v.grantors.$each[index]) {
        this.$v.grantors.$each[index].$reset();
        // Re-touch the status field since we just changed it
        this.$v.grantors.$each[index].status.$touch();
      }
    },
    
    addGrantor() {
      this.grantors.push(createEmptyGrantor());
    },
    
    removeGrantor(index) {
      if (this.grantors.length > 1) {
        this.grantors.splice(index, 1);
      }
    },
    
    standardizeDateOfDeath(index) {
      if (!this.grantors[index].dateOfDeath) return;
      
      let date = this.grantors[index].dateOfDeath.replace(/[^0-9]/g, '');
      if (date.length === 8) {
        // Format as MM/DD/YYYY
        date = date.slice(0, 2) + '/' + date.slice(2, 4) + '/' + date.slice(4, 8);
        this.grantors[index].dateOfDeath = date;
      }
    },
    
    formatZipCode(index) {
      let value = this.grantors[index].address.zipCode;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ##### or #####-####
      if (value.length > 5) {
        value = value.slice(0, 5) + '-' + value.slice(5, 9);
      }
      
      this.grantors[index].address.zipCode = value;
      this.$v.grantors.$each[index].address.zipCode.$touch();
    },
    
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      // Transform grantors data to match backend expectations
      const transformedGrantors = this.grantors.map(grantor => {
        const grantorData = {
          isAlive: grantor.status === 'Alive',
          firstName: grantor.firstName,
          lastName: grantor.lastName,
          citizenship: grantor.citizenship,
          sourceOfWealth: grantor.sourceOfWealth,
        };
        
        if (grantor.status === 'Alive') {
          // Include fields for alive grantors
          grantorData.address = {
            line1: grantor.address.street,
            city: grantor.address.city,
            state: grantor.address.state,
            zip: grantor.address.zipCode,
            country: grantor.address.country,
          };
          grantorData.employmentStatus = grantor.employmentStatus;
          grantorData.employerIndustry = grantor.employerIndustry;
          grantorData.annualIncome = grantor.annualIncome;
        } else {
          // Include dateOfDeath for deceased grantors
          grantorData.dateOfDeath = grantor.dateOfDeath;
        }
        
        return grantorData;
      });

      const data = {
        instance: this.instance,
        grantors: transformedGrantors,
      };

      this.markPageAsSyncing(MENUS.CLIENT.GRANTORS.id, this.instance);

      this.completeStep(MENUS.CLIENT.GRANTORS.id, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    skip() {
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    toggleFormState(hide) {
      this.formHidden = hide;
      
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    handleSyncStatusChange() {
      if (this.isGrantorsComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    
    isGrantorsComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    // Get the current page index based on name and instance
    this.pages = this.interviewPages.getInterviewPages();
    
    // Find the index of this specific grantors page
    this.pageIndex = this.pages.findIndex(page => {
      if (typeof page === 'object' && page.name === MENUS.CLIENT.GRANTORS.id) {
        return page.params && page.params.name === this.name && page.params.label === this.label;
      }
      return false;
    });

    this.formHidden = this.isGrantorsComplete;
    
    // Load existing data if available
    const existingPage = this.getPage(MENUS.CLIENT.GRANTORS.id, this.instance);
    if (existingPage && existingPage.data && existingPage.data.grantors) {
      this.grantors = existingPage.data.grantors;
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.isGrantorsComplete) {
        this.formHidden = true;
      }
    });
    
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>